import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Card,
    CardContent,
    Grid,
    TextField,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Collapse,
} from '@mui/material';
import {
    Upload as UploadIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { workshopDomain } from '../../util/domain';
import { parseCSVRows } from '../../util/csvParse';

/** DB column is `year`; show as YOP in the UI */
const FIELD_LABELS = {
    name: 'NAME',
    email: 'EMAIL',
    mobile: 'MOBILE',
    degree: 'DEGREE',
    year: 'YOP (saved as year)',
    college_name: 'COLLEGE NAME',
    branch: 'BRANCH',
    state: 'STATE',
    college_code: 'COLLEGE CODE',
    gender: 'GENDER',
    city: 'CITY',
};

const mappingSelectValue = (mapping, field) => {
    const v = mapping[field];
    if (v === '' || v === null || v === undefined) return '';
    return v;
};

const StudentUpload = () => {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [csvFile, setCsvFile] = useState(null);
    const [csvHeaders, setCsvHeaders] = useState([]);
    const [csvPreview, setCsvPreview] = useState([]);
    const [fieldMapping, setFieldMapping] = useState({
        name: '',
        email: '',
        mobile: '',
        degree: '',
        year: '',
        college_name: '',
        branch: '',
        state: '',
        college_code: '',
        gender: '',
        city: '',
    });
    const [mappedPreview, setMappedPreview] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [uploadResults, setUploadResults] = useState(null);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [expandedRows, setExpandedRows] = useState({});

    const requiredFields = ['name', 'email', 'mobile', 'degree', 'year', 'college_name', 'branch', 'state'];
    const optionalFields = ['college_code', 'gender', 'city'];

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/admin');
            return;
        }

        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const roles = user.roles || [];
        const isAdmin = roles.includes('Admin');
        if (!isAdmin) {
            navigate('/admin/home');
            return;
        }

        fetchEvents();
    }, [navigate]);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${workshopDomain}/api/events/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setEvents(response.data.events);
            }
        } catch (error) {
            console.error('Error fetching events:', error);
            showNotification('Failed to fetch events', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showNotification = (message, severity) => {
        setNotification({ open: true, message, severity });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
            showNotification('Please select a CSV file', 'error');
            return;
        }

        setCsvFile(file);
        parseCSV(file);
    };

    const parseCSV = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target.result;
            const rows = parseCSVRows(content);

            if (rows.length === 0) {
                showNotification('CSV file is empty', 'error');
                return;
            }

            const headers = rows[0];
            setCsvHeaders(headers);

            // Preview first 10 data rows (quoted commas preserved per field)
            const preview = rows.slice(1, 11).filter((r) => r.some((c) => c && c.length > 0));
            setCsvPreview(preview);

            // Auto-map fields if headers match common patterns
            const autoMapping = {};
            headers.forEach((header, index) => {
                const lowerHeader = header.toLowerCase().trim();
                const compact = lowerHeader.replace(/[^a-z0-9]/g, '');
                if (lowerHeader.includes('name') && !lowerHeader.includes('college')) {
                    autoMapping.name = index;
                } else if (lowerHeader.includes('email')) {
                    autoMapping.email = index;
                } else if (lowerHeader.includes('mobile') || lowerHeader.includes('phone')) {
                    autoMapping.mobile = index;
                } else if (lowerHeader.includes('degree')) {
                    autoMapping.degree = index;
                } else if (
                    lowerHeader.includes('year') ||
                    lowerHeader.includes('yop') ||
                    compact === 'yop' ||
                    compact.includes('yop') ||
                    compact.includes('yearofpass') ||
                    compact.includes('passingyear') ||
                    compact.includes('batch')
                ) {
                    autoMapping.year = index;
                } else if (lowerHeader.includes('college') && !lowerHeader.includes('code')) {
                    autoMapping.college_name = index;
                } else if (lowerHeader.includes('college') && lowerHeader.includes('code')) {
                    autoMapping.college_code = index;
                } else if (lowerHeader.includes('branch')) {
                    autoMapping.branch = index;
                } else if (lowerHeader.includes('state')) {
                    autoMapping.state = index;
                } else if (lowerHeader.includes('gender')) {
                    autoMapping.gender = index;
                } else if (lowerHeader.includes('city')) {
                    autoMapping.city = index;
                }
            });

            setFieldMapping(prev => ({ ...prev, ...autoMapping }));
            setPreviewDialogOpen(true);
        };
        reader.readAsText(file);
    };

    const generateMappedPreview = () => {
        if (!csvPreview.length) return [];

        const mapped = csvPreview.map((row, rowIndex) => {
            const mappedRow = {};
            Object.keys(fieldMapping).forEach(field => {
                const columnIndex = fieldMapping[field];
                if (columnIndex !== '' && columnIndex !== null && columnIndex !== undefined && !Number.isNaN(Number(columnIndex))) {
                    const idx = Number(columnIndex);
                    mappedRow[field] = row[idx] ?? '';
                } else {
                    mappedRow[field] = '';
                }
            });
            return { rowIndex: rowIndex + 1, ...mappedRow };
        });

        setMappedPreview(mapped);
        return mapped;
    };

    const validateMapping = () => {
        const missingFields = requiredFields.filter(field => {
            const columnIndex = fieldMapping[field];
            return columnIndex === '' || columnIndex === null || columnIndex === undefined || Number.isNaN(Number(columnIndex));
        });

        if (missingFields.length > 0) {
            showNotification(`Please map all required fields: ${missingFields.join(', ')}`, 'error');
            return false;
        }

        return true;
    };

    const handleUpload = async () => {
        if (!selectedEvent) {
            showNotification('Please select an event', 'error');
            return;
        }

        if (!csvFile) {
            showNotification('Please select a CSV file', 'error');
            return;
        }

        // Validations removed - allow upload regardless of field mapping

        try {
            setUploading(true);
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('csvFile', csvFile);
            formData.append('fieldMapping', JSON.stringify(fieldMapping));

            const response = await axios.post(
                `${workshopDomain}/api/registration/bulk-upload/${selectedEvent.hash_id}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.success) {
                setUploadResults(response.data);
                showNotification(response.data.message || 'Upload completed successfully', 'success');
                setCsvFile(null);
                setCsvHeaders([]);
                setCsvPreview([]);
                setMappedPreview([]);
                setFieldMapping({
                    name: '',
                    email: '',
                    mobile: '',
                    degree: '',
                    year: '',
                    college_name: '',
                    branch: '',
                    state: '',
                    college_code: '',
                    gender: '',
                    city: '',
                });
                setPreviewDialogOpen(false);
            } else {
                showNotification(response.data.message || 'Upload failed', 'error');
            }
        } catch (error) {
            console.error('Error uploading CSV:', error);
            showNotification(error.response?.data?.message || 'Failed to upload CSV', 'error');
        } finally {
            setUploading(false);
        }
    };

    const downloadResults = () => {
        if (!uploadResults) return;

        const results = [
            ['Row', 'Status', 'Message'],
            ...uploadResults.results.failed.map(r => [r.row || '', 'Failed', r.reason || '']),
            ...uploadResults.results.skipped.map(r => [r.row || '', 'Skipped', r.reason || '']),
        ];

        const csvContent = results.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `upload-results-${Date.now()}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const toggleRowExpansion = (rowIndex) => {
        setExpandedRows(prev => ({
            ...prev,
            [rowIndex]: !prev[rowIndex]
        }));
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Bulk Student Upload
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                    Upload a CSV file to bulk register students for an event
                </Typography>

                <Card sx={{ mt: 3 }}>
                    <CardContent>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Select Event</InputLabel>
                                    <Select
                                        value={selectedEvent?.hash_id || ''}
                                        label="Select Event"
                                        onChange={(e) => {
                                            const event = events.find(ev => ev.hash_id === e.target.value);
                                            setSelectedEvent(event);
                                        }}
                                    >
                                        {events.map(event => (
                                            <MenuItem key={event.hash_id} value={event.hash_id}>
                                                {event.title} ({event.event_type})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Button
                                    variant="contained"
                                    component="label"
                                    startIcon={<UploadIcon />}
                                    fullWidth
                                    sx={{ height: '56px' }}
                                >
                                    {csvFile ? csvFile.name : 'Upload CSV File'}
                                    <input
                                        type="file"
                                        hidden
                                        accept=".csv"
                                        onChange={handleFileChange}
                                    />
                                </Button>
                            </Grid>
                        </Grid>

                        {csvFile && (
                            <Box sx={{ mt: 3 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        generateMappedPreview();
                                        setPreviewDialogOpen(true);
                                    }}
                                >
                                    Preview & Map Fields
                                </Button>
                            </Box>
                        )}

                        {csvFile && selectedEvent && (
                            <Box sx={{ mt: 3 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleUpload}
                                    disabled={uploading}
                                    startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                                >
                                    {uploading ? 'Uploading...' : 'Upload Students'}
                                </Button>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                {/* Field Mapping Dialog */}
                <Dialog
                    open={previewDialogOpen}
                    onClose={() => setPreviewDialogOpen(false)}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogTitle>Map CSV Columns to Database Fields</DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Required Fields:
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                {requiredFields.map(field => (
                                    <Grid item xs={12} sm={6} md={4} key={field}>
                                        <FormControl fullWidth>
                                            <InputLabel>{FIELD_LABELS[field] || field.replace(/_/g, ' ').toUpperCase()}</InputLabel>
                                            <Select
                                                value={mappingSelectValue(fieldMapping, field)}
                                                label={FIELD_LABELS[field] || field.replace(/_/g, ' ').toUpperCase()}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setFieldMapping(prev => ({
                                                        ...prev,
                                                        [field]: v === '' ? '' : Number(v),
                                                    }));
                                                }}
                                            >
                                                <MenuItem value="">-- Select Column --</MenuItem>
                                                {csvHeaders.map((header, index) => (
                                                    <MenuItem key={index} value={index}>
                                                        Column {index + 1}: {header}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                ))}
                            </Grid>

                            <Typography variant="subtitle2" gutterBottom>
                                Optional Fields:
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                {optionalFields.map(field => (
                                    <Grid item xs={12} sm={6} md={4} key={field}>
                                        <FormControl fullWidth>
                                            <InputLabel>{FIELD_LABELS[field] || field.replace(/_/g, ' ').toUpperCase()}</InputLabel>
                                            <Select
                                                value={mappingSelectValue(fieldMapping, field)}
                                                label={FIELD_LABELS[field] || field.replace(/_/g, ' ').toUpperCase()}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setFieldMapping(prev => ({
                                                        ...prev,
                                                        [field]: v === '' ? '' : Number(v),
                                                    }));
                                                }}
                                            >
                                                <MenuItem value="">-- Select Column --</MenuItem>
                                                {csvHeaders.map((header, index) => (
                                                    <MenuItem key={index} value={index}>
                                                        Column {index + 1}: {header}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                ))}
                            </Grid>

                            {mappedPreview.length > 0 && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Preview (First 10 rows):
                                    </Typography>
                                    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Row</TableCell>
                                                    {requiredFields.map(field => (
                                                        <TableCell key={field}>{FIELD_LABELS[field] || field.replace(/_/g, ' ')}</TableCell>
                                                    ))}
                                                    {optionalFields.map(field => (
                                                        <TableCell key={field}>{FIELD_LABELS[field] || field.replace(/_/g, ' ')}</TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {mappedPreview.map((row, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell>{row.rowIndex}</TableCell>
                                                        {requiredFields.map(field => (
                                                            <TableCell key={field}>{row[field] || '-'}</TableCell>
                                                        ))}
                                                        {optionalFields.map(field => (
                                                            <TableCell key={field}>{row[field] || '-'}</TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            generateMappedPreview();
                        }}>
                            Refresh Preview
                        </Button>
                        <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Upload Results */}
                {uploadResults && (
                    <Card sx={{ mt: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">Upload Results</Typography>
                                {uploadResults.results.failed.length > 0 && (
                                    <Button
                                        startIcon={<DownloadIcon />}
                                        onClick={downloadResults}
                                        variant="outlined"
                                    >
                                        Download Errors
                                    </Button>
                                )}
                            </Box>

                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={4}>
                                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
                                        <Typography variant="h4">{uploadResults.results.total}</Typography>
                                        <Typography variant="body2">Total Processed</Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                                        <Typography variant="h4">{uploadResults.results.success.length}</Typography>
                                        <Typography variant="body2">Successfully Created</Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                                        <Typography variant="h4">{uploadResults.results.skipped.length}</Typography>
                                        <Typography variant="body2">Skipped (Duplicates)</Typography>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {uploadResults.results.failed.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Failed Rows ({uploadResults.results.failed.length}):
                                    </Typography>
                                    <TableContainer component={Paper}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Row</TableCell>
                                                    <TableCell>Reason</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {uploadResults.results.failed.map((item, idx) => (
                                                    <React.Fragment key={idx}>
                                                        <TableRow>
                                                            <TableCell>{item.row || idx + 1}</TableCell>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <ErrorIcon color="error" fontSize="small" />
                                                                    {item.reason || 'Unknown error'}
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    </React.Fragment>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}

                            {uploadResults.results.skipped.length > 0 && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Skipped Rows ({uploadResults.results.skipped.length}):
                                    </Typography>
                                    <TableContainer component={Paper}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Row</TableCell>
                                                    <TableCell>Reason</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {uploadResults.results.skipped.map((item, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell>{item.row || idx + 1}</TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <CheckCircleIcon color="warning" fontSize="small" />
                                                                {item.reason || 'Duplicate student or registration'}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                )}
            </Box>

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={notification.severity} onClose={() => setNotification({ ...notification, open: false })}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default StudentUpload;
